import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import { useEffect, useState } from 'react';
import HeroModel from './components/HeroModel';
import CarModel from './components/CarModel';

function useCursorFX() {
  useEffect(() => {
    const cursor = document.querySelector('.cursor');
    const ring = document.querySelector('.cursor-ring');
    if (!cursor || !ring) return;

    const isCoarse = window.matchMedia('(pointer: coarse)').matches;
    if (isCoarse) {
      cursor.style.display = 'none';
      ring.style.display = 'none';
      document.body.style.cursor = 'auto';
      return;
    }

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX;
    let ringY = mouseY;

    const onMove = (event) => {
      mouseX = event.clientX;
      mouseY = event.clientY;
      cursor.style.transform = `translate(${mouseX}px, ${mouseY}px) translate(-50%, -50%)`;
    };

    window.addEventListener('mousemove', onMove);

    let raf;
    const animate = () => {
      ringX += (mouseX - ringX) * 0.15;
      ringY += (mouseY - ringY) * 0.15;
      ring.style.transform = `translate(${ringX}px, ${ringY}px) translate(-50%, -50%)`;
      raf = requestAnimationFrame(animate);
    };
    animate();

    const interactive = document.querySelectorAll('a, button, .card, .gallery-card, .sponsor-card');
    interactive.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        ring.style.width = '54px';
        ring.style.height = '54px';
      });
      item.addEventListener('mouseleave', () => {
        ring.style.width = '34px';
        ring.style.height = '34px';
      });
    });

    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);
}

function useRevealOnScroll() {
  useEffect(() => {
    const revealEls = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    revealEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

function FloatingField({ items, className }) {
  return (
    <div className={className} aria-hidden="true">
      {items.map((item) => (
        <span
          key={item.id}
          className={item.type}
          style={{
            left: item.x,
            top: item.y,
            animationDelay: item.delay,
            animationDuration: item.duration,
          }}
        >
          {item.char}
        </span>
      ))}
    </div>
  );
}

const globalAscii = [
  { id: 1, char: 'TRACE', x: '6%', y: '18%', delay: '0s', duration: '9s', type: 'float-ascii' },
  { id: 2, char: 'SPEED', x: '82%', y: '26%', delay: '1.1s', duration: '8s', type: 'float-ascii' },
  { id: 3, char: 'Σ', x: '20%', y: '62%', delay: '2.2s', duration: '10s', type: 'float-ascii' },
  { id: 4, char: 'NODE', x: '70%', y: '74%', delay: '1.8s', duration: '9.5s', type: 'float-ascii' },
  { id: 5, char: 'GRID', x: '48%', y: '40%', delay: '0.7s', duration: '8.6s', type: 'float-ascii' },
];

const globalPixels = [
  { id: 1, char: '▓▓', x: '14%', y: '34%', delay: '0.6s', duration: '8s', type: 'float-pixel' },
  { id: 2, char: '▒▒', x: '58%', y: '20%', delay: '1.4s', duration: '7.8s', type: 'float-pixel' },
  { id: 3, char: '██', x: '86%', y: '60%', delay: '2.1s', duration: '9s', type: 'float-pixel' },
  { id: 4, char: '▓░', x: '34%', y: '78%', delay: '1.7s', duration: '8.4s', type: 'float-pixel' },
];

function Landing() {
  useRevealOnScroll();

  const asciiItems = [
    { id: 1, char: '<>', x: '6%', y: '14%', delay: '0s', duration: '8s', type: 'float-ascii' },
    { id: 2, char: '{ }', x: '72%', y: '12%', delay: '1s', duration: '9s', type: 'float-ascii' },
    { id: 3, char: '0101', x: '85%', y: '36%', delay: '2s', duration: '7s', type: 'float-ascii' },
    { id: 4, char: 'CAD', x: '12%', y: '62%', delay: '0.5s', duration: '10s', type: 'float-ascii' },
    { id: 5, char: 'RACE', x: '58%', y: '74%', delay: '1.5s', duration: '9s', type: 'float-ascii' },
    { id: 6, char: 'CFD', x: '42%', y: '26%', delay: '2.5s', duration: '8s', type: 'float-ascii' },
    { id: 7, char: 'ΔV', x: '22%', y: '40%', delay: '1.2s', duration: '7.5s', type: 'float-ascii' },
    { id: 8, char: 'AERO', x: '78%', y: '58%', delay: '2.8s', duration: '9.5s', type: 'float-ascii' },
    { id: 9, char: '//', x: '30%', y: '18%', delay: '0.8s', duration: '8.5s', type: 'float-ascii' },
    { id: 10, char: 'Q1', x: '10%', y: '82%', delay: '1.7s', duration: '7.8s', type: 'float-ascii' },
    { id: 11, char: 'DRAG', x: '88%', y: '22%', delay: '2.2s', duration: '8.8s', type: 'float-ascii' },
    { id: 12, char: 'FLOW', x: '52%', y: '10%', delay: '1.9s', duration: '9.2s', type: 'float-ascii' },
  ];

  const pixelItems = [
    { id: 1, char: '▓▓', x: '18%', y: '22%', delay: '0s', duration: '7s', type: 'float-pixel' },
    { id: 2, char: '▒▒', x: '64%', y: '30%', delay: '1s', duration: '8s', type: 'float-pixel' },
    { id: 3, char: '██', x: '78%', y: '68%', delay: '1.6s', duration: '9s', type: 'float-pixel' },
    { id: 4, char: '▓▒', x: '30%', y: '78%', delay: '2.2s', duration: '8.5s', type: 'float-pixel' },
    { id: 5, char: '░░', x: '10%', y: '44%', delay: '1.4s', duration: '7.6s', type: 'float-pixel' },
    { id: 6, char: '▓░', x: '48%', y: '18%', delay: '0.8s', duration: '8.2s', type: 'float-pixel' },
    { id: 7, char: '▒▓', x: '86%', y: '50%', delay: '2.8s', duration: '7.4s', type: 'float-pixel' },
    { id: 8, char: '██', x: '36%', y: '56%', delay: '1.9s', duration: '8.9s', type: 'float-pixel' },
    { id: 9, char: '▓▓', x: '6%', y: '70%', delay: '2.6s', duration: '7.2s', type: 'float-pixel' },
  ];

  const sectionAscii = [
    { id: 1, char: 'VECTOR', x: '8%', y: '120%', delay: '0s', duration: '11s', type: 'float-ascii' },
    { id: 2, char: '01', x: '78%', y: '140%', delay: '1.2s', duration: '10s', type: 'float-ascii' },
    { id: 3, char: 'RPM', x: '24%', y: '180%', delay: '2s', duration: '12s', type: 'float-ascii' },
    { id: 4, char: 'LAUNCH', x: '60%', y: '210%', delay: '1.6s', duration: '11.5s', type: 'float-ascii' },
    { id: 5, char: 'SPLIT', x: '88%', y: '230%', delay: '2.4s', duration: '10.5s', type: 'float-ascii' },
  ];

  const sectionPixels = [
    { id: 1, char: '▓▓', x: '16%', y: '150%', delay: '0.6s', duration: '9s', type: 'float-pixel' },
    { id: 2, char: '▒▒', x: '70%', y: '170%', delay: '1.4s', duration: '8.5s', type: 'float-pixel' },
    { id: 3, char: '██', x: '40%', y: '220%', delay: '2.2s', duration: '9.2s', type: 'float-pixel' },
    { id: 4, char: '▓░', x: '90%', y: '260%', delay: '1.8s', duration: '8.8s', type: 'float-pixel' },
  ];

  return (
    <div className="page">
      <section className="hero-section" id="top">
        <HeroModel />
        <div className="pixel-grid" aria-hidden="true"></div>
        <FloatingField items={asciiItems} className="float-field" />
        <FloatingField items={pixelItems} className="float-field" />

        <div className="container hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">STEM Racing • 2026 Season</p>
            <h1>Longhorns Racing</h1>
            <p className="lead">
              We engineer speed at the edge of physics. Aerodynamics, rapid iteration, and relentless precision.
            </p>
            <div className="cta-row">
              <a className="btn btn-crimson" href="#about">Enter the Grid</a>
              <Link className="btn btn-outline-light" to="/gallery">Gallery</Link>
            </div>
            <div className="stat-row">
              <div>
                <span className="stat-value">0.002s</span>
                <span className="stat-label">Split-time ambitions</span>
              </div>
              <div>
                <span className="stat-value">20+</span>
                <span className="stat-label">CAD iterations</span>
              </div>
              <div>
                <span className="stat-value">4</span>
                <span className="stat-label">Subteams</span>
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-hint">Scroll</div>
      </section>
      <Link className="full-cta" to="/project-cruel-angel">
        Discover Project Cruel Angel
      </Link>

      <section className="panel panorama-panel" id="panorama">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Panorama</p>
            <h2>Race Moments</h2>
            <p>Rotating panorama from our latest events.</p>
          </div>
        </div>
        <div className="panorama-track">
          <div className="panorama-marquee">
            {[
              '/gallery/IMG_0525.jpeg',
              '/gallery/PHOTO-2025-04-09-19-08-39.jpeg',
              '/gallery/IMG_6830.jpeg',
              '/gallery/IMG_6829.jpeg',
              '/gallery/IMG_4181.jpeg',
            ].map((src, idx) => (
              <img key={src + idx} src={src} alt="Longhorns Racing panorama" />
            ))}
            {[
              '/gallery/IMG_0525.jpeg',
              '/gallery/PHOTO-2025-04-09-19-08-39.jpeg',
              '/gallery/IMG_6830.jpeg',
              '/gallery/IMG_6829.jpeg',
              '/gallery/IMG_4181.jpeg',
            ].map((src, idx) => (
              <img key={`dup-${idx}`} src={src} alt="" aria-hidden="true" />
            ))}
          </div>
        </div>
      </section>

      <FloatingField items={sectionAscii} className="float-field section-field" />
      <FloatingField items={sectionPixels} className="float-field section-field" />

      <section className="panel showcase-panel" id="showcase">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Showcase</p>
            <h2>Rotating Highlights</h2>
            <p>Gallery highlights rotating below the CAD hero.</p>
          </div>
        </div>
        <div className="showcase-frame reveal">
          {[
            { src: '/gallery/IMG_0525.jpeg', label: 'Regional Finals Awards' },
            { src: '/gallery/PHOTO-2025-04-09-19-08-39.jpeg', label: 'Stage Moment' },
            { src: '/gallery/IMG_6830.jpeg', label: 'Team Photo' },
            { src: '/gallery/IMG_6829.jpeg', label: 'Championship Group' },
            { src: '/gallery/IMG_4181.jpeg', label: 'Team Display' },
          ].map((item, idx) => (
            <figure className="showcase-slide" key={item.src} style={{ animationDelay: `${idx * 3.8}s` }}>
              <img src={item.src} alt={item.label} />
              <figcaption>{item.label}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="panel" id="about">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Mission</p>
            <h2>Build. Test. Win.</h2>
            <p>We’re a STEM Racing team focused on advanced aerodynamics, material science, and the art of precision manufacturing.</p>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <article className="card reveal">
                <h3>Design Lab</h3>
                <p>Parametric CAD, CFD loops, and iterative wind tunnel analysis to sculpt the cleanest airflow possible.</p>
              </article>
            </div>
            <div className="col-md-4">
              <article className="card reveal">
                <h3>Build Bay</h3>
                <p>Laser-precise manufacturing with smart tooling, rapid prototyping, and obsessive surface finish control.</p>
              </article>
            </div>
            <div className="col-md-4">
              <article className="card reveal">
                <h3>Race Ops</h3>
                <p>Telemetry-driven testing and ultra-fast adjustments that keep us ahead on race day.</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="panel dark" id="timeline">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Roadmap</p>
            <h2>Season Timeline</h2>
            <p>We run a tight cadence from concept to podium.</p>
          </div>
          <div className="timeline">
            <div className="timeline-item reveal">
              <span className="time-tag">Phase 01</span>
              <h3>Concept & CFD</h3>
              <p>Airfoil experiments, drag reduction, and flow visualization.</p>
            </div>
            <div className="timeline-item reveal">
              <span className="time-tag">Phase 02</span>
              <h3>Fabrication</h3>
              <p>Precision machining, surface finishing, and assembly.</p>
            </div>
            <div className="timeline-item reveal">
              <span className="time-tag">Phase 03</span>
              <h3>Optimization</h3>
              <p>Track testing, telemetry review, and final tweaks.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="panel" id="sponsors">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Partners</p>
            <h2>Back the Longhorns</h2>
            <p>Help us bring advanced STEM education to life while building championship tech.</p>
          </div>
          <div className="row g-4">
            {[
              'Suppliers and shops that keep our fabrication and materials pipeline strong.',
              'Backers who help cover travel, registration, and competition costs.',
              'Mentors from industry and alumni who sharpen our design reviews.',
              'Faculty and administrators who advocate for STEM racing on campus.',
            ].map((item, idx) => (
              <div className="col-6 col-md-3" key={idx}>
                <div className="sponsor-card reveal">{item}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function About() {
  useRevealOnScroll();
  return (
    <div className="page">
      <section className="panel hero-section">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">About Us</p>
            <h2>Precision, Teamwork, Velocity.</h2>
            <p>Longhorns Racing blends engineering rigor with race-day execution. We build the car, build each other, and chase the fastest possible time.</p>
          </div>
          <div className="row g-4">
            <div className="col-md-6">
              <article className="card reveal">
                <h3>Our Origin</h3>
                <p>Founded to elevate STEM learning through competition, we operate like a compact motorsport lab: design, simulate, build, and compete.</p>
              </article>
            </div>
            <div className="col-md-6">
              <article className="card reveal">
                <h3>How We Work</h3>
                <p>Rapid iteration cycles, weekly sprints, and constant feedback from data keep us moving fast with purpose.</p>
              </article>
            </div>
          </div>
          <Link className="btn btn-crimson mt-4" to="/">Back to Launch</Link>
        </div>
      </section>
    </div>
  );
}

function Awards() {
  useRevealOnScroll();
  return (
    <div className="page">
      <section className="panel hero-section">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Awards</p>
            <h2>Recognition & Milestones</h2>
            <p>Track record across national and state competitions.</p>
          </div>
          <div className="row g-4">
            {[
              {
                title: '2x National Qualifiers',
                body: 'Earned national bids in back-to-back seasons—consistent car performance and strong design defense across both runs.',
              },
              {
                title: '2nd Place Nationally',
                body: 'Finished on the national podium, combining track execution with how we presented data, trade-offs, and test history.',
              },
              {
                title: '2x Georgia State Champions',
                body: 'Twice topped Georgia—out-engineering and out-racing the state field with a program built on iteration and teamwork.',
              },
              {
                title: '3x Best Verbal Presentation',
                body: 'Judges repeatedly highlighted how we explain aero choices, manufacturing constraints, and race strategy under pressure.',
              },
              {
                title: '1x Digital Media Award',
                body: 'Recognized for photo, video, and graphics that document the build and show the human side of STEM racing.',
              },
            ].map((item, idx) => (
              <div className="col-md-6" key={idx}>
                <div className="card reveal">
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
          <Link className="btn btn-crimson mt-4" to="/">Back to Launch</Link>
        </div>
      </section>
    </div>
  );
}

function Sponsorship() {
  useRevealOnScroll();
  const sponsorList = [
    {
      name: 'Jane Street',
      logo: '/sponsors/jane-street.png',
      desc: 'Precision partner supporting engineering rigor and competition readiness.',
    },
    {
      name: 'Eye Level',
      logo: '/sponsors/images.png',
      desc: 'Learning partner backing STEM growth and academic excellence.',
    },
    {
      name: 'Cielo Vision',
      logo: '/sponsors/288669.jpeg',
      desc: 'Community partner fueling our season and build momentum.',
    },
    {
      name: 'Altitude Trampoline Park',
      logo: '/sponsors/images (1).png',
      desc: 'Local sponsor supporting team energy and outreach events.',
    },
    {
      name: 'Laplace College Consulting',
      logo: '/sponsors/68649-removebg-preview.png',
      desc: 'Mentorship partner investing in student futures and college success.',
    },
  ];
  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    form.classList.add('was-validated');
    if (form.checkValidity()) {
      form.reset();
      form.classList.remove('was-validated');
      alert('Thanks for reaching out — we will get back to you shortly.');
    }
  };
  return (
    <div className="page">
      <section className="panel hero-section">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Sponsorship</p>
            <h2>Partner With Longhorns Racing</h2>
            <p>Support the next generation of engineers with direct, visible impact. We provide branding, mentorship moments, and measurable reach.</p>
          </div>
          <div className="row g-4">
            {sponsorList.map((sponsor) => (
              <div className="col-md-4" key={sponsor.name}>
                <div className="card reveal sponsor-card-detail">
                  <div className="sponsor-logo">
                    {sponsor.logo ? (
                      <img src={sponsor.logo} alt={`${sponsor.name} logo`} />
                    ) : (
                      <span>{sponsor.name}</span>
                    )}
                  </div>
                  <h3>{sponsor.name}</h3>
                  <p>{sponsor.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="card reveal">
                <h3>Starter</h3>
                <p>Logo on digital assets, team updates, and behind-the-scenes access.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card reveal">
                <h3>Prime</h3>
                <p>Logo on car body, event shoutouts, and student workshops.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card reveal">
                <h3>Title</h3>
                <p>Top billing, co-branded media, and exclusive lab demos.</p>
              </div>
            </div>
          </div>
          <div className="sponsor-form reveal">
            <h3>Sponsorship Inquiry</h3>
            <form className="needs-validation" noValidate onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Name</label>
                  <input type="text" className="form-control" required />
                  <div className="invalid-feedback">Please enter your name.</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Organization</label>
                  <input type="text" className="form-control" required />
                  <div className="invalid-feedback">Please enter your organization.</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" required />
                  <div className="invalid-feedback">Please enter a valid email.</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Sponsorship Level</label>
                  <select className="form-select" required>
                    <option value="">Choose...</option>
                    <option>Starter</option>
                    <option>Prime</option>
                    <option>Title</option>
                    <option>Custom</option>
                  </select>
                  <div className="invalid-feedback">Please select a level.</div>
                </div>
                <div className="col-12">
                  <label className="form-label">Message</label>
                  <textarea className="form-control" rows="4" required></textarea>
                  <div className="invalid-feedback">Tell us how you want to collaborate.</div>
                </div>
              </div>
              <button className="btn btn-crimson mt-4" type="submit">Send Inquiry</button>
            </form>
          </div>
          <Link className="btn btn-crimson mt-4" to="/">Back to Launch</Link>
        </div>
      </section>
    </div>
  );
}

function Cars() {
  useRevealOnScroll();
  return (
    <div className="page">
      <section className="panel hero-section">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Cars</p>
            <h2>Meet the Fleet</h2>
            <p>Wireframe CAD previews for LR01-A, LR01-B, and LR02.</p>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="card reveal car-card">
                <h3>LR01-A</h3>
                <CarModel src="/models/lr01-a/lr01-a.glb" label="LR01-A" scale={70} cameraZ={220} />
                <p>Top speed • Drag coefficient • Mass • Material stack</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card reveal car-card">
                <h3>LR01-B</h3>
                <CarModel src="/models/lr01-b/lr01-b.glb" label="LR01-B" scale={70} cameraZ={220} />
                <p>Top speed • Drag coefficient • Mass • Material stack</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card reveal car-card">
                <h3>LR02</h3>
                <CarModel src="/models/main-asm.stl" label="LR02" scale={95} cameraZ={190} />
                <p>Top speed • Drag coefficient • Mass • Material stack</p>
              </div>
            </div>
          </div>
          <Link className="btn btn-crimson mt-4" to="/">Back to Launch</Link>
        </div>
      </section>
    </div>
  );
}

function Gallery() {
  useRevealOnScroll();
  const galleryItems = [
    {
      src: '/gallery/IMG_0525.jpeg',
      title: 'Regional Finals Awards',
      caption: 'Hardware from regionals—two trophies on the table after a clean run through design and racing phases.',
    },
    {
      src: '/gallery/PHOTO-2025-04-09-19-08-39.jpeg',
      title: 'Stage Moment',
      caption: 'On stage with the car when results are called—years of build work condensed into one announcement.',
    },
    {
      src: '/gallery/IMG_6830.jpeg',
      title: 'Team Photo',
      caption: 'Full roster in team kits before competition—every subteam represented from aero to race ops.',
    },
    {
      src: '/gallery/IMG_6829.jpeg',
      title: 'Championship Group',
      caption: 'Championship weekend crew shot—travel, setup, and race day wrapped into one memory.',
    },
    {
      src: '/gallery/IMG_4181.jpeg',
      title: 'Team Display',
      caption: 'Pit display and car presentation—how we tell the engineering story to judges and the public.',
    },
    {
      src: '/gallery/render 2.png',
      title: 'LR02 Render',
      caption: 'Red LR02 render showing full chassis proportions and front wing geometry.',
    },
    {
      src: '/gallery/render3.png',
      title: 'LR02 Render (Rear)',
      caption: 'Red LR02 rear view highlighting diffuser depth and wheel alignment.',
    },
    {
      src: '/gallery/render 4.png',
      title: 'LR02 Render (Side)',
      caption: 'Red LR02 side profile focused on body taper and aero surface continuity.',
    },
    {
      src: '/gallery/Screenshot 2025-12-03 010240.png',
      title: 'LR01 CAD Screenshot 01',
      caption: 'White LR01 top view from the CAD model review and annotation pass.',
    },
    {
      src: '/gallery/Screenshot 2025-12-03 005754.png',
      title: 'LR01 CAD Screenshot 02',
      caption: 'White LR01 assembly view showing component fit checks and mechanical clearances.',
    },
    {
      src: '/gallery/Screenshot 2025-12-03 005059.png',
      title: 'LR01 CAD Screenshot 03',
      caption: 'White LR01 surface refinement focused on flow paths and fillet transitions.',
    },
    {
      src: '/gallery/Screenshot 2025-12-03 004445.png',
      title: 'LR01 CAD Screenshot 04',
      caption: 'White LR01 angle study for canopy and nose profile adjustments.',
    },
    {
      src: '/gallery/Screenshot 2025-12-03 004058 (1).png',
      title: 'LR01 CAD Screenshot 05',
      caption: 'White LR01 top-down review for symmetry, mounting points, and wheel alignment.',
    },
  ];
  return (
    <div className="page gallery">
      <section className="panel hero-section">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Gallery</p>
            <h2>Build Highlights</h2>
            <p>Photos from awards nights, on-stage moments, and the displays we bring to competition—people first, car in frame.</p>
          </div>
          <div className="row g-4">
            {galleryItems.map((item) => (
              <div className="col-md-4" key={item.src}>
                <div className="gallery-card reveal">
                  <div className="gallery-image">
                    <img src={item.src} alt={item.title} />
                  </div>
                  <div className="gallery-meta">
                    <h3>{item.title}</h3>
                    <p>{item.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Link className="btn btn-crimson mt-4" to="/">Back to Launch</Link>
        </div>
      </section>
    </div>
  );
}

function ProjectCruelAngel() {
  useRevealOnScroll();
  return (
    <div className="page">
      <section className="panel hero-section">
        <div className="container">
          <div className="panel-header reveal">
            <p className="eyebrow">Nationals 2026</p>
            <h2>Project Cruel Angel</h2>
            <p>Our flagship nationals car for 2026 — designed for extreme efficiency, precision aerodynamics, and controlled stability.</p>
          </div>
          <div className="row g-4">
            <div className="col-md-6">
              <div className="card reveal">
                <h3>Design Intent</h3>
                <p>Ultra-low drag surfaces, reduced wake turbulence, and a stability-first centerline built for maximum straight-line performance.</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card reveal">
                <h3>Performance Targets</h3>
                <p>Weight minimized through advanced materials, rapid prototyping cycles, and a final tuning pass guided by telemetry data.</p>
              </div>
            </div>
          </div>
          <Link className="btn btn-crimson mt-4" to="/">Back to Launch</Link>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  useCursorFX();
  const [introDone, setIntroDone] = useState(false);
  return (
    <BrowserRouter>
      <div className="cursor" aria-hidden="true"></div>
      <div className="cursor-ring" aria-hidden="true"></div>
      {!introDone && (
        <div className="intro-overlay" onAnimationEnd={() => setIntroDone(true)}>
          <div className="intro-line">LONGHORNS RACING</div>
          <div className="intro-subline">SYSTEM BOOT • RACE PROTOCOL</div>
          <div className="magi-grid">
            <div className="magi-core">MAGI-1</div>
            <div className="magi-core">MAGI-2</div>
            <div className="magi-core">MAGI-3</div>
          </div>
          <div className="intro-bar">
            <span></span>
          </div>
          <div className="magi-lines">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="intro-glow"></div>
        </div>
      )}
      <div className="global-dither" aria-hidden="true">
        <div className="pixel-grid"></div>
        <FloatingField items={globalAscii} className="float-field" />
        <FloatingField items={globalPixels} className="float-field" />
      </div>

      <nav className="navbar navbar-expand-lg navbar-dark nav-blur">
        <div className="container">
          <Link className="navbar-brand brand-lockup" to="/">
            <img src="/logo.png" alt="Longhorns Racing logo" />
            <span>Longhorns Racing</span>
          </Link>
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navMenu">
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navMenu">
            <div className="navbar-nav ms-auto">
              <Link className="nav-link" to="/">Home</Link>
              <Link className="nav-link" to="/gallery">Gallery</Link>
              <Link className="nav-link" to="/cars">Cars</Link>
              <Link className="nav-link" to="/about">About</Link>
              <Link className="nav-link" to="/awards">Awards</Link>
              <Link className="nav-link" to="/sponsorship">Sponsorship</Link>
              <a className="nav-link" href="#sponsors">Sponsors</a>
            </div>
          </div>
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/cars" element={<Cars />} />
        <Route path="/about" element={<About />} />
        <Route path="/awards" element={<Awards />} />
        <Route path="/sponsorship" element={<Sponsorship />} />
        <Route path="/project-cruel-angel" element={<ProjectCruelAngel />} />
      </Routes>
    </BrowserRouter>
  );
}
